// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Customise Home > Swipe actions. A live row plays the swipe you are editing (and can
// be swiped by hand on a phone), each side shows its short and long action as coloured pills,
// tapping a pill opens the picker of tinted tiles below (like Settings > Mail > Swipe actions),
// then the feel: haptics, the short-swipe tray and how far a swipe goes before it arms.

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {MailSwipeRow, type MailSwipeSpec} from '@app/features/konvo_mail/components/MailSwipeRow';
import {
	HOME_SWIPE_ACTION_LABEL,
	HOME_SWIPE_ACTION_TONE,
	homeSwipeActionIcon,
} from '@app/features/konvo_today/components/HomeSwipeActionMeta';
import styles from '@app/features/konvo_today/components/settings/CustomiseHome.module.css';
import {
	HOME_SWIPE_DIRECTION_LABEL,
	HOME_SWIPE_MAIL_TONE,
	HOME_SWIPE_REACH_LABEL,
} from '@app/features/konvo_today/components/settings/CustomiseHomeMeta';
import HomePreferences, {
	HOME_SWIPE_ACTIONS,
	HOME_SWIPE_LONG_RATIO,
	type HomeSwipeAction,
	type HomeSwipeDirection,
	type HomeSwipeDistance,
	type HomeSwipeReach,
} from '@app/features/konvo_today/state/HomePreferences';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {
	ArrowLeftIcon,
	ArrowRightIcon,
	ArrowsLeftRightIcon,
	HandTapIcon,
	KanbanIcon,
	VibrateIcon,
} from '@app/features/ui/icons/Icons';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useId, useState} from 'react';

const SWIPES_DESCRIPTOR = msg({
	message: 'Swipe actions',
	comment: 'Customise Home section title: what swiping a Home row left or right does.',
});
const SWIPES_PHONE_NOTE_DESCRIPTOR = msg({
	message: 'Swipes work on touch screens, like your phone. Each device keeps its own.',
	comment: 'Customise Home > Swipe actions: note shown on desktop, where rows are not swiped with a mouse.',
});
const PREVIEW_DESCRIPTOR = msg({
	message: 'Preview: {side} · {reach}',
	comment:
		'Customise Home > Swipe actions: caption above the live preview row. {side} is "Swipe right" or "Swipe left", {reach} is "Short" or "Long".',
});
const PREVIEW_TRY_DESCRIPTOR = msg({
	message: 'Try it on the row',
	comment: 'Customise Home > Swipe actions: the preview row can be swiped by hand on a phone.',
});
const SHORT_LONG_DESCRIPTOR = msg({
	message: 'Short · long',
	comment: 'Customise Home > Swipe actions: line under "Swipe right" / "Swipe left" naming its two pills.',
});
const PICK_SLOT_DESCRIPTOR = msg({
	message: '{side}, {reach}: {action}',
	comment:
		'Accessible label of an action pill in Customise Home > Swipe actions, e.g. "Swipe right, Short: Done". Opens the picker.',
});
const PICKER_TITLE_DESCRIPTOR = msg({
	message: '{side} · {reach}',
	comment: 'Customise Home > Swipe actions: label above the action picker, e.g. "Swipe right · Short".',
});
const FEEL_DESCRIPTOR = msg({
	message: 'Feel',
	comment: 'Customise Home section title: haptics, the short-swipe tray and swipe distance.',
});
const HAPTICS_DESCRIPTOR = msg({message: 'Haptics', comment: 'Customise Home > Feel: switch for the vibration tick.'});
const HAPTICS_HINT_DESCRIPTOR = msg({
	message: 'A tick at each line',
	comment: 'Customise Home > Feel: line under Haptics. A swipe has two lines (short and long).',
});
const TRAY_DESCRIPTOR = msg({
	message: 'Short-swipe tray',
	comment: 'Customise Home > Feel: switch for the tray of actions a short swipe left rests open on.',
});
const TRAY_HINT_DESCRIPTOR = msg({
	message: '{first} and {second} on a short pull',
	comment: 'Customise Home > Feel: line under Short-swipe tray. {first} and {second} are action names like Later.',
});
const TRAY_OFF_DESCRIPTOR = msg({
	message: 'A short pull springs back',
	comment: 'Customise Home > Feel: line under Short-swipe tray while it is off.',
});
const DISTANCE_DESCRIPTOR = msg({
	message: 'Swipe distance',
	comment: 'Customise Home > Feel: how far a swipe goes before it arms.',
});
const DISTANCE_HINT_DESCRIPTOR = msg({
	message: 'How far until it arms',
	comment: 'Customise Home > Feel: line under Swipe distance.',
});
const DISTANCE_LABEL: Record<HomeSwipeDistance, MessageDescriptor> = {
	short: msg({message: 'Short', comment: 'Customise Home > Feel > Swipe distance: the shortest setting.'}),
	medium: msg({message: 'Medium', comment: 'Customise Home > Feel > Swipe distance: the middle setting.'}),
	long: msg({message: 'Long', comment: 'Customise Home > Feel > Swipe distance: the longest setting.'}),
};
const SAMPLE_NAME = 'Lerato';
const SAMPLE_TEXT_DESCRIPTOR = msg({
	message: 'Answer on Teams about the kiosk demo',
	comment: 'Sample to-do on the Customise Home swipe preview row. Any short to-do works.',
});

const DISTANCES: ReadonlyArray<HomeSwipeDistance> = ['short', 'medium', 'long'];
const DIRECTIONS: ReadonlyArray<HomeSwipeDirection> = ['right', 'left'];
const REACHES: ReadonlyArray<HomeSwipeReach> = ['short', 'long'];

export interface HomeSwipeSlot {
	direction: HomeSwipeDirection;
	reach: HomeSwipeReach;
}

function toneOf(action: HomeSwipeAction): string {
	return action === 'none' ? 'var(--text-tertiary, var(--text-secondary))' : HOME_SWIPE_ACTION_TONE[action];
}

/**
 * The row under test. Editing a short slot, it carries both lines (a hand swipe on a phone
 * reaches the long one too) and the demo pulls just past the first line. Editing a long slot,
 * the long action sits on the first line at the long distance so the demo shows it.
 */
const PreviewRow = observer(({slot}: {slot: HomeSwipeSlot}) => {
	const {i18n} = useLingui();
	const spec = (action: HomeSwipeAction): MailSwipeSpec | null =>
		action === 'none'
			? null
			: {
					key: action,
					label: i18n._(HOME_SWIPE_ACTION_LABEL[action]),
					icon: homeSwipeActionIcon(action, 22),
					tone: HOME_SWIPE_MAIL_TONE[action],
					color: HOME_SWIPE_ACTION_TONE[action],
					removes: false,
					run: () => undefined,
				};
	const prefs = HomePreferences;
	const long = slot.reach === 'long';
	const specs = (direction: HomeSwipeDirection) => ({
		first: spec(prefs.swipeAction(direction, slot.reach)),
		second: long ? null : spec(prefs.swipeAction(direction, 'long')),
	});
	const right = specs('right');
	const left = specs('left');
	const tray =
		!long && prefs.trayEnabled
			? [left.first, left.second].filter((entry): entry is MailSwipeSpec => entry !== null)
			: undefined;
	return (
		<div className={styles.preview}>
			<div className={styles.previewCaption}>
				<HandTapIcon size={15} />
				<span>
					{i18n._(PREVIEW_DESCRIPTOR, {
						side: i18n._(HOME_SWIPE_DIRECTION_LABEL[slot.direction]),
						reach: i18n._(HOME_SWIPE_REACH_LABEL[slot.reach]),
					})}
				</span>
				{MobileLayout.enabled ? <span className={styles.previewTry}>{i18n._(PREVIEW_TRY_DESCRIPTOR)}</span> : null}
			</div>
			<MailSwipeRow
				key={`${slot.direction}.${slot.reach}`}
				variant="card"
				className={styles.previewRow}
				leading={right.first}
				leadingLong={right.second}
				trailing={left.first}
				trailingLong={left.second}
				trailingTray={tray}
				longRatio={HOME_SWIPE_LONG_RATIO}
				commitRatio={long ? HOME_SWIPE_LONG_RATIO : prefs.distanceRatio}
				haptics={prefs.haptics}
				demo={slot.direction === 'right' ? 'leading' : 'trailing'}
				data-flx="konvo-today.customise-home.swipe-preview"
			>
				<div className={styles.fakeRow}>
					<i className={styles.fakeTick} aria-hidden="true" />
					<span className={styles.fakeFace} aria-hidden="true">
						L
					</span>
					<span className={styles.fakeText}>
						<b>{SAMPLE_NAME}</b> {i18n._(SAMPLE_TEXT_DESCRIPTOR)}
					</span>
				</div>
			</MailSwipeRow>
		</div>
	);
});

function ActionPill({
	action,
	solid,
	selected,
	label,
	onClick,
	'data-flx': dataFlx,
}: {
	action: HomeSwipeAction;
	solid: boolean;
	selected: boolean;
	label: string;
	onClick: () => void;
	'data-flx': string;
}) {
	const {i18n} = useLingui();
	return (
		<button
			type="button"
			className={clsx(styles.pill, solid && action !== 'none' && styles.pillSolid, selected && styles.pillSelected)}
			style={{'--pill-tone': toneOf(action)} as React.CSSProperties}
			aria-label={label}
			aria-expanded={selected}
			onClick={onClick}
			data-flx={dataFlx}
		>
			{homeSwipeActionIcon(action, 15)}
			<span>{i18n._(HOME_SWIPE_ACTION_LABEL[action])}</span>
		</button>
	);
}

export const HomeSwipeActionPicker = observer(({slot}: {slot: HomeSwipeSlot}) => {
	const {i18n} = useLingui();
	const current = HomePreferences.swipeAction(slot.direction, slot.reach);
	const title = i18n._(PICKER_TITLE_DESCRIPTOR, {
		side: i18n._(HOME_SWIPE_DIRECTION_LABEL[slot.direction]),
		reach: i18n._(HOME_SWIPE_REACH_LABEL[slot.reach]),
	});
	return (
		<div className={styles.pickerBlock}>
			<div className={styles.subLabel}>{title}</div>
			<div
				className={clsx(styles.card, styles.tiles)}
				role="radiogroup"
				aria-label={title}
				data-flx="konvo-today.customise-home.swipe-picker"
			>
				{HOME_SWIPE_ACTIONS.map((action) => {
					const on = action === current;
					return (
						<button
							key={action}
							type="button"
							role="radio"
							aria-checked={on}
							className={clsx(styles.tile2, on && styles.tile2On)}
							style={{'--tile-tone': toneOf(action)} as React.CSSProperties}
							onClick={() => HomePreferences.setSwipeAction(slot.direction, slot.reach, action)}
							data-flx={`konvo-today.customise-home.swipe-picker.${action}`}
						>
							<span className={styles.tile2Well} aria-hidden="true">
								{homeSwipeActionIcon(action, 22)}
							</span>
							<span className={styles.tile2Label}>{i18n._(HOME_SWIPE_ACTION_LABEL[action])}</span>
						</button>
					);
				})}
			</div>
		</div>
	);
});

export const SwitchRow = ({
	icon,
	title,
	description,
	value,
	onChange,
	'data-flx': dataFlx,
}: {
	icon: React.ReactNode;
	title: string;
	description: string;
	value: boolean;
	onChange: (value: boolean) => void;
	'data-flx': string;
}) => {
	const labelId = useId();
	return (
		<div className={styles.row} data-flx={dataFlx}>
			<span className={styles.tile} aria-hidden="true">
				{icon}
			</span>
			<span className={styles.text}>
				<span id={labelId} className={styles.title}>
					{title}
				</span>
				<span className={styles.description}>{description}</span>
			</span>
			<Switch compact value={value} onChange={onChange} ariaLabelledBy={labelId} data-flx={`${dataFlx}.switch`} />
		</div>
	);
};

const FeelCard = observer(() => {
	const {i18n} = useLingui();
	const prefs = HomePreferences;
	const distanceId = useId();
	const index = Math.max(0, DISTANCES.indexOf(prefs.distance));
	return (
		<div className={styles.card} data-flx="konvo-today.customise-home.feel">
			<SwitchRow
				icon={<VibrateIcon size={20} />}
				title={i18n._(HAPTICS_DESCRIPTOR)}
				description={i18n._(HAPTICS_HINT_DESCRIPTOR)}
				value={prefs.haptics}
				onChange={prefs.setHaptics}
				data-flx="konvo-today.customise-home.haptics"
			/>
			<SwitchRow
				icon={<KanbanIcon size={20} />}
				title={i18n._(TRAY_DESCRIPTOR)}
				description={
					prefs.trayEnabled
						? i18n._(TRAY_HINT_DESCRIPTOR, {
								first: i18n._(HOME_SWIPE_ACTION_LABEL[prefs.swipeAction('left', 'short')]),
								second: i18n._(HOME_SWIPE_ACTION_LABEL[prefs.swipeAction('left', 'long')]),
							})
						: i18n._(TRAY_OFF_DESCRIPTOR)
				}
				value={prefs.trayEnabled}
				onChange={prefs.setTrayEnabled}
				data-flx="konvo-today.customise-home.tray"
			/>
			<div className={styles.distanceBlock}>
				<div className={clsx(styles.row, styles.rowFlat)}>
					<span className={styles.tile} aria-hidden="true">
						<ArrowsLeftRightIcon size={20} />
					</span>
					<span className={styles.text}>
						<span id={distanceId} className={styles.title}>
							{i18n._(DISTANCE_DESCRIPTOR)}
						</span>
						<span className={styles.description}>{i18n._(DISTANCE_HINT_DESCRIPTOR)}</span>
					</span>
				</div>
				<div className={styles.slider}>
					<span aria-hidden="true">{i18n._(DISTANCE_LABEL.short)}</span>
					<input
						type="range"
						min={0}
						max={DISTANCES.length - 1}
						step={1}
						value={index}
						onChange={(event) => prefs.setDistance(DISTANCES[Number(event.target.value)] ?? 'medium')}
						aria-labelledby={distanceId}
						aria-valuetext={i18n._(DISTANCE_LABEL[DISTANCES[index]])}
						className={styles.range}
						style={{'--range-fill': `${(index / (DISTANCES.length - 1)) * 100}%`} as React.CSSProperties}
						data-flx="konvo-today.customise-home.distance"
					/>
					<span aria-hidden="true">{i18n._(DISTANCE_LABEL.long)}</span>
				</div>
			</div>
		</div>
	);
});

export const HomeSwipeSettings = observer(() => {
	const {i18n} = useLingui();
	const [slot, setSlot] = useState<HomeSwipeSlot>({direction: 'right', reach: 'short'});
	const prefs = HomePreferences;
	return (
		<>
			<SettingsSection
				id="konvo-home-swipes"
				title={i18n._(SWIPES_DESCRIPTOR)}
				description={MobileLayout.enabled ? undefined : i18n._(SWIPES_PHONE_NOTE_DESCRIPTOR)}
				className={styles.bare}
			>
				<div className={styles.stack}>
					<div className={styles.card} data-flx="konvo-today.customise-home.swipes">
						<PreviewRow slot={slot} />
						{DIRECTIONS.map((direction) => {
							const side = i18n._(HOME_SWIPE_DIRECTION_LABEL[direction]);
							return (
								<div key={direction} className={styles.row} data-flx={`konvo-today.customise-home.swipe-${direction}`}>
									<span className={styles.tile} aria-hidden="true">
										{direction === 'right' ? <ArrowRightIcon size={20} /> : <ArrowLeftIcon size={20} />}
									</span>
									<span className={styles.text}>
										<span className={styles.title}>{side}</span>
										<span className={styles.description}>{i18n._(SHORT_LONG_DESCRIPTOR)}</span>
									</span>
									<span className={styles.pills}>
										{REACHES.map((reach) => {
											const action = prefs.swipeAction(direction, reach);
											return (
												<ActionPill
													key={reach}
													action={action}
													solid={reach === 'short'}
													selected={slot.direction === direction && slot.reach === reach}
													label={i18n._(PICK_SLOT_DESCRIPTOR, {
														side,
														reach: i18n._(HOME_SWIPE_REACH_LABEL[reach]),
														action: i18n._(HOME_SWIPE_ACTION_LABEL[action]),
													})}
													onClick={() => setSlot({direction, reach})}
													data-flx={`konvo-today.customise-home.swipe-${direction}.${reach}`}
												/>
											);
										})}
									</span>
								</div>
							);
						})}
					</div>
					<HomeSwipeActionPicker slot={slot} />
				</div>
			</SettingsSection>
			<SettingsSection id="konvo-home-swipe-feel" title={i18n._(FEEL_DESCRIPTOR)} className={styles.bare}>
				<FeelCard />
			</SettingsSection>
		</>
	);
});
